import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';

export const ImpressumScreen: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 sm:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-accent-pink hover:text-accent-purple mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('impressum.back')}
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="prose prose-gray max-w-none"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8">
            {t('impressum.title')}
          </h1>
          <p className="text-sm text-gray-500 mb-6">{t('impressum.tmgHeader')}</p>
          <p className="text-sm text-gray-700 mb-8">{t('impressum.companyDesc')}</p>

          <section className="space-y-6 text-gray-700">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('impressum.companyName')}
              </h2>
              <div className="flex items-start gap-3 mt-2">
                <MapPin className="w-5 h-5 text-accent-pink flex-shrink-0 mt-0.5" />
                <div>
                  <p>Zeilweg 17-19</p>
                  <p>60439 Frankfurt am Main</p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm mb-2">
                <strong>{t('impressum.handelsregister')}:</strong> HRB140337
              </p>
              <p className="text-sm mb-2">
                <strong>{t('impressum.registergericht')}:</strong> Amtsgericht Frankfurt
              </p>
              <p className="text-sm mb-2">
                <strong>{t('impressum.representedBy')}:</strong>
              </p>
              <p className="text-sm ml-4 mb-2">
                {t('impressum.geschaeftsfuehrerin')}: Jasmina Regeljac
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                {t('impressum.contact')}
              </h2>
              <div className="flex items-center gap-3 mb-2">
                <Phone className="w-5 h-5 text-accent-pink flex-shrink-0" />
                <div>
                  <p className="text-sm">
                    <strong>{t('impressum.phone')}:</strong>{' '}
                    <a href="tel:+4969271339100" className="text-accent-pink hover:underline">
                      069 / 271 339 100
                    </a>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <Mail className="w-5 h-5 text-accent-pink flex-shrink-0" />
                <div>
                  <p className="text-sm">
                    <strong>{t('impressum.fax')}:</strong> 069 / 271 339 1299
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-accent-pink flex-shrink-0" />
                <div>
                  <p className="text-sm">
                    <strong>E-Mail:</strong>{' '}
                    <a href="mailto:info@suton-radio.de" className="text-accent-pink hover:underline">
                      info@suton-radio.de
                    </a>
                    {' / '}
                    <a href="mailto:info@suton-media.de" className="text-accent-pink hover:underline">
                      info@suton-media.de
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm mb-2">
                <strong>{t('impressum.vatId')}</strong>
              </p>
              <p className="text-sm">{t('impressum.vatIdDesc')}: DE353319688</p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm mb-2">
                <strong>{t('impressum.editorialResponsible')}</strong>
              </p>
              <p className="text-sm">
                SUTON Media GmbH<br />
                Zeilweg 17-19<br />
                60439 Frankfurt am Main
              </p>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('impressum.euDispute')}
              </h2>
              <p className="text-sm">
                {t('impressum.euDisputeDesc')}:{' '}
                <a
                  href="https://ec.europa.eu/consumers/odr/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-pink hover:underline"
                >
                  https://ec.europa.eu/consumers/odr/
                </a>
              </p>
              <p className="text-sm mt-2">{t('impressum.euDisputeRefuse')}</p>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('impressum.contentLiability')}
              </h2>
              <p className="text-sm">{t('impressum.contentLiabilityDesc')}</p>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('impressum.linkLiability')}
              </h2>
              <p className="text-sm">{t('impressum.linkLiabilityDesc')}</p>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {t('impressum.copyright')}
              </h2>
              <p className="text-sm">{t('impressum.copyrightDesc')}</p>
            </div>
          </section>
        </motion.article>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-accent-pink hover:text-accent-purple transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('impressum.back')}
          </Link>
        </div>
      </div>
    </div>
  );
};
